/**
 * Every server read the dashboard performs. Screens consume these hooks and
 * never touch axios directly.
 *
 * The list endpoint may answer either as a bare array or as the shared
 * `Paginated<T>` envelope while the API settles; `unwrapList` accepts both so a
 * server-side change cannot blank a screen.
 */

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { KycSnapshot, Paginated, TraderProfile } from '@shared/types';
import type {
  CommissionRate,
  LedgerEntry,
  TraderBalances,
  TrafficStatus,
  WithdrawalRequest,
} from '@shared/ledger';
import { api } from './api';
import { endpoints } from './endpoints';
import { queryKeys, type LedgerFilters } from './queryKeys';
import type {
  ActiveSession,
  BalanceSnapshot,
  NotificationPrefs,
  TraderStats,
  TraderTransaction,
} from '@/types/api';

const POLL_MS = 5000;

function unwrapList<T>(payload: T[] | Paginated<T> | null | undefined): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

/**
 * The mirror of `unwrapList` for screens that need the total as well as the
 * page: a bare array is treated as the whole result set, which is what it is.
 */
function unwrapPage<T>(
  payload: T[] | Paginated<T> | null | undefined,
  limit: number,
  offset: number,
): Paginated<T> {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, limit, offset };
  }
  if (payload && Array.isArray(payload.items)) return payload;
  return { items: [], total: 0, limit, offset };
}

export function useProfile(): UseQueryResult<TraderProfile> {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const { data } = await api.get<TraderProfile | { trader: TraderProfile }>(
        endpoints.auth.profile,
      );
      return 'trader' in data ? data.trader : data;
    },
    staleTime: 60_000,
  });
}

export function useBalance(): UseQueryResult<BalanceSnapshot> {
  return useQuery({
    queryKey: queryKeys.balance,
    queryFn: async () => {
      const { data } = await api.get<BalanceSnapshot>(endpoints.balance);
      return data;
    },
    staleTime: 15_000,
  });
}

export function useStats(): UseQueryResult<TraderStats> {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: async () => {
      const { data } = await api.get<TraderStats>(endpoints.stats);
      return data;
    },
    staleTime: 15_000,
  });
}

export function useKyc(): UseQueryResult<KycSnapshot> {
  return useQuery({
    queryKey: queryKeys.kyc,
    queryFn: async () => {
      const { data } = await api.get<KycSnapshot>(endpoints.kyc.status);
      return data;
    },
    staleTime: 30_000,
  });
}

interface TransactionsOptions {
  limit?: number;
  /** Traffic polls; the dashboard's recent-activity list does not need to. */
  live?: boolean;
}

export function useTransactions({
  limit = 50,
  live = false,
}: TransactionsOptions = {}): UseQueryResult<TraderTransaction[]> {
  return useQuery({
    queryKey: queryKeys.transactions.list(limit),
    queryFn: async () => {
      const { data } = await api.get<TraderTransaction[] | Paginated<TraderTransaction>>(
        endpoints.transactions.list,
        { params: { limit, offset: 0 } },
      );
      return unwrapList(data);
    },
    refetchInterval: live ? POLL_MS : false,
    // Keep polling while the trader alt-tabs to their banking app; that is
    // exactly when a payment is most likely to land.
    refetchIntervalInBackground: live,
    staleTime: live ? 0 : 10_000,
  });
}

export function useSessions(): UseQueryResult<ActiveSession[]> {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: async () => {
      const { data } = await api.get<ActiveSession[] | Paginated<ActiveSession>>(
        endpoints.account.sessions,
      );
      return unwrapList(data);
    },
    staleTime: 60_000,
  });
}

export function useNotificationPrefs(): UseQueryResult<NotificationPrefs> {
  return useQuery({
    queryKey: queryKeys.notificationPrefs,
    queryFn: async () => {
      const { data } = await api.get<NotificationPrefs>(endpoints.account.notificationPrefs);
      return data;
    },
    staleTime: 60_000,
  });
}

// ---------- The money layer ----------

/**
 * The four figures on Финансы, straight off the ledger.
 *
 * Kept short-lived rather than cached for a minute: `frozen` moves every time a
 * payment is routed to the trader, and a stale "заморожено" is the figure most
 * likely to be misread as money that has gone missing.
 */
export function useTraderBalances(): UseQueryResult<TraderBalances> {
  return useQuery({
    queryKey: queryKeys.money.balances,
    queryFn: async () => {
      const { data } = await api.get<TraderBalances>(endpoints.money.balances);
      return data;
    },
    staleTime: 10_000,
  });
}

/**
 * Whether payments are being routed to this trader, and if not, why.
 *
 * Polled, not merely cached. The blocking reason can change without the trader
 * doing anything — the last free card hits its daily ceiling, collateral runs
 * out under a large payment — and the switch must never sit there claiming
 * «приём включён» after the server has stopped agreeing.
 */
export function useTrafficStatus(): UseQueryResult<TrafficStatus> {
  return useQuery({
    queryKey: queryKeys.money.traffic,
    queryFn: async () => {
      const { data } = await api.get<TrafficStatus>(endpoints.money.traffic);
      return data;
    },
    staleTime: 5_000,
    refetchInterval: 30_000,
  });
}

/** Rates per method. Changes about never, so it is cached for the session. */
export function useCommissionRates(): UseQueryResult<CommissionRate[]> {
  return useQuery({
    queryKey: queryKeys.money.commission,
    queryFn: async () => {
      const { data } = await api.get<CommissionRate[] | Paginated<CommissionRate>>(
        endpoints.money.commission,
      );
      return unwrapList(data);
    },
    staleTime: 30 * 60_000,
  });
}

/**
 * The trader's own statement.
 *
 * Paged by growing `limit` rather than by walking `offset`: the ledger is
 * append-only at the head, so an offset-paged second page would silently skip
 * whatever settled while the trader was reading the first one. Growing the
 * window re-reads from the top and cannot drop a row. `keepPreviousData` means
 * «Показать ещё» extends the list instead of blanking it.
 */
export function useLedger(filters: LedgerFilters): UseQueryResult<Paginated<LedgerEntry>> {
  const { account, kind, limit } = filters;

  return useQuery({
    queryKey: queryKeys.money.ledger.list(filters),
    queryFn: async () => {
      const { data } = await api.get<LedgerEntry[] | Paginated<LedgerEntry>>(
        endpoints.money.ledger,
        {
          params: {
            limit,
            offset: 0,
            ...(account ? { account } : {}),
            ...(kind ? { kind } : {}),
          },
        },
      );
      return unwrapPage(data, limit, 0);
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** Withdrawal history, paged the same way and for the same reason. */
export function useWithdrawals(limit: number): UseQueryResult<Paginated<WithdrawalRequest>> {
  return useQuery({
    queryKey: queryKeys.money.withdrawals.list(limit),
    queryFn: async () => {
      const { data } = await api.get<WithdrawalRequest[] | Paginated<WithdrawalRequest>>(
        endpoints.money.withdrawals,
        { params: { limit, offset: 0 } },
      );
      return unwrapPage(data, limit, 0);
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
